import { activeStorageBlobUrl } from "@circle-react/helpers/urlHelpers";
import { useDirectUpload } from "@circle-react/hooks/useDirectUpload";
import type { Editor } from "@tiptap/react";
import { defer } from "lodash";
import { useEffect, useState } from "react";

import { giphyToFile } from "./giphyToFile";
import { insertImageNode } from "./insertImageNode";
import type {
  AddToLocalInlineAttachments,
  EditorRange,
  GiphyItem,
} from "./types";

interface UploadResponse {
  content_type: string;
  filename: string;
  signed_id: string;
}

interface UseGiphyUploadParams {
  addToLocalInlineAttachments: AddToLocalInlineAttachments;
  editor: Editor;
  onComplete: () => void;
  range: EditorRange;
}

export const useGiphyUpload = ({
  editor,
  range,
  addToLocalInlineAttachments,
  onComplete,
}: UseGiphyUploadParams) => {
  const [isPreparing, setIsPreparing] = useState(false);
  const { uploadFile, status, isError, response, progress } = useDirectUpload();

  const isUploading = !isError && status === "uploading";
  const isProcessing = isUploading || isPreparing;

  useEffect(() => {
    if (status === "uploading") setIsPreparing(false);
    if (status !== "completed" || !response) return;

    const { signed_id, filename, content_type } = response as UploadResponse;
    const attachment = {
      url: activeStorageBlobUrl({ signed_id, filename }),
      signed_id,
      content_type,
    };

    addToLocalInlineAttachments(attachment);
    defer(() => {
      insertImageNode({ editor, range, attachment });
      onComplete();
    });
  }, [status, response]);

  const addGif = async (gif: GiphyItem) => {
    setIsPreparing(true);
    const file = await giphyToFile(gif);
    uploadFile(file);
  };

  return { addGif, isProcessing, progress };
};
